import { useState } from "react";
import { Offcanvas } from "react-bootstrap";
import { useApiMutation } from "../../../utils/api";
import { genericError } from "../../../utils/errors";
import { SubmitButton } from "../../../utils/submit_button";

const todayStr = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const formatDateDisplay = (dateStr: string): string => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const adjustDate = (dateStr: string, days: number): string => {
  const [year, month, day] = dateStr.split("-").map(Number);
  const d = new Date(year, month - 1, day + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

interface LogWeighingModalProps {
  close: () => void;
  refetch: () => void;
  weighing?: { id: number; weighed_on: string; weight_oz: number | null };
  lastKnownWeightOz?: number | null;
}

export const LogWeighingModal = ({
  close,
  refetch,
  weighing,
  lastKnownWeightOz,
}: LogWeighingModalProps) => {
  const { post, saving } = useApiMutation();
  const [weighedOn, setWeighedOn] = useState(weighing?.weighed_on ?? todayStr());
  const [totalOz, setTotalOz] = useState(weighing?.weight_oz ?? lastKnownWeightOz ?? 128);

  const handleSubmit = () => {
    const payload = { weighed_on: weighedOn, weight_oz: totalOz };
    const request = weighing
      ? post(`/api/user/update_weighing/${weighing.id}`, payload)
      : post("/api/user/create_weighing", payload);

    request.then(({ data }) => {
      if (data.success) {
        refetch();
        close();
      } else {
        genericError();
      }
    });
  };

  return (
    <Offcanvas show onHide={close} placement="bottom" className="h-100">
      <Offcanvas.Header
        closeButton
        className="border-0 pb-0 justify-content-center"
      >
        <Offcanvas.Title className="w-100 text-center fs-32">
          {weighing ? "Edit Weighing" : "Add Weighing"}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="text-center py-4">
        {/* Date */}
        <div className="d-flex align-items-center justify-content-center gap-2">
          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => setWeighedOn((d) => adjustDate(d, -1))}
          >
            <i className="bi bi-chevron-left fs-48 fs-m-24" />
          </button>

          <div className="pt-4 pb-4 pl-3 pr-3">
            <div className="fs-64 fs-m-32 fw-bold">
              {formatDateDisplay(weighedOn)}
            </div>
          </div>

          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => setWeighedOn((d) => adjustDate(d, 1))}
          >
            <i className="bi bi-chevron-right fs-48 fs-m-24" />
          </button>
        </div>

        {/* Weight */}
        <div className="d-flex align-items-center justify-content-center gap-2">
          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => setTotalOz((p) => Math.max(0, p - 1))}
          >
            <i className="bi bi-chevron-left fs-48 fs-m-24" />
          </button>

          <div className="pt-4 pb-4 pl-3 pr-3">
            <div className="fs-64 fs-m-32 fw-bold">
              {Math.floor(totalOz / 16)} lbs {totalOz % 16} oz
            </div>
          </div>

          <button
            type="button"
            className="btn btn-text btn-lg"
            onClick={() => setTotalOz((p) => p + 1)}
          >
            <i className="bi bi-chevron-right fs-48 fs-m-24" />
          </button>
        </div>

        <div className="d-flex mt-5 justify-content-center container">
          <SubmitButton
            loading={saving}
            className="btn btn-primary btn-lg col-12 col-sm-3 fs-28"
            onClick={handleSubmit}
          >
            Save
          </SubmitButton>
        </div>
        <div className="d-flex mt-5">
          <button
            type="button"
            className="btn btn-text btn-lg px-5 mx-auto text-muted"
            onClick={close}
          >
            Cancel
          </button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};
